import _ from 'lodash'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Divider,
  Grid,
  makeStyles,
  MenuItem,
  Typography,
} from '@material-ui/core'
import React, { useEffect, useRef } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { ChevronDown } from 'react-feather'
import { ControlledTextField } from '../../../form/ControlledTextField'
import { theme } from '../../../../theme'
import { validators } from '../../../../validation/validators'
import { airFlowUnit, temperatureUnit } from '../../../../util/formatters'
import {
  ScheduleFragment,
  UpdateEnergySystemInput,
  UpdateScheduleInput,
  VentilationDeviceType,
} from '../../../../generated/graphql'
import {
  DeviceData,
  mapFormValuesToInput as esFormValuesToInput,
} from '../../energy-system/form-values'
import {
  mapFormValuesToInputForCalibration as scheduleCalFormValuesToInput,
  mapFormValuesToInput as scheduleFormValuesToInput,
} from '../../schedule/form-values'
import { VentilationInputTabFormValues } from './ventilation-form-values'
import { ScheduleHoursRow, ScheduleMonthsRow } from './InputScheduleComponents'

const useAccordionStyles = makeStyles({
  root: {
    border: 'thin solid lightgray',
    boxShadow: 'none',
    marginBottom: theme.spacing(2),
  },
  expanded: {
    backgroundColor: 'white',
  },
})

type Props = {
  id: string
  schedules: ScheduleFragment[]
  defaultValues: VentilationInputTabFormValues
  onUpdate: (
    esInput: UpdateEnergySystemInput,
    scheduleId?: string,
    scheduleInput?: UpdateScheduleInput,
  ) => void
  onDbUpdate: (
    esId: string,
    esInput: UpdateEnergySystemInput,
    scheduleIds?: string[],
    scheduleInputs?: UpdateScheduleInput[],
  ) => Promise<void>
}

export function CalibrationInputVentilationTab({
  id,
  schedules,
  defaultValues,
  onUpdate,
  onDbUpdate,
}: Props): React.ReactElement {
  const { t } = useTranslation()

  const accordionClasses = useAccordionStyles()

  const { control, errors, reset, formState } =
    useForm<VentilationInputTabFormValues>({
      defaultValues,
      mode: 'onBlur',
    })

  const dbUpdateNeededRef = useRef(false)

  const deviceDataRef = useRef(
    defaultValues.values.parameters.ventilation?.deviceData as DeviceData[],
  )

  const updateModelData = async (
    values: VentilationInputTabFormValues,
    modifiedDeviceIndex?: number,
  ) => {
    const { isDirty } = formState
    const isValid = await control.trigger()
    if (isValid && isDirty) {
      const updateValues = { ...defaultValues }
      _.merge(updateValues, values)

      const energySystemInput = esFormValuesToInput(updateValues.values)

      // Update device related schedule

      if (
        updateValues.values.parameters.ventilation?.deviceData &&
        defaultValues.values.parameters.ventilation?.deviceData &&
        updateValues.deviceScheduleValues &&
        modifiedDeviceIndex !== undefined
      ) {
        const deviceSchedule =
          updateValues.deviceScheduleValues[modifiedDeviceIndex]

        const scheduleId =
          updateValues.values.parameters.ventilation.deviceData[
            modifiedDeviceIndex
          ].deviceScheduleId

        const scheduleInput = scheduleCalFormValuesToInput(deviceSchedule)

        onUpdate(energySystemInput, scheduleId, scheduleInput)

        reset(updateValues)

        dbUpdateNeededRef.current = true
      }
    }
  }

  const saveChanges = async () => {
    if (!control.defaultValuesRef.current) return

    const currentValues = control.defaultValuesRef
      .current as VentilationInputTabFormValues

    const energySystemInput = esFormValuesToInput(currentValues.values)

    const scheduleInputs: UpdateScheduleInput[] = []

    const scheduleIds: string[] = []

    if (currentValues.values.parameters.ventilation?.deviceData) {
      const devices = currentValues.values.parameters.ventilation.deviceData
      for (let i = 0; i < devices.length; i++) {
        const device = devices[i]
        if (device.deviceScheduleId) scheduleIds.push(device.deviceScheduleId)
      }
    }

    if (
      currentValues.deviceScheduleValues &&
      currentValues.deviceScheduleValues.length > 0
    ) {
      for (let i = 0; i < currentValues.deviceScheduleValues.length; i++)
        scheduleInputs.push(
          scheduleFormValuesToInput(currentValues.deviceScheduleValues[i]),
        )
    }

    await onDbUpdate(id, energySystemInput, scheduleIds, scheduleInputs)
  }

  useEffect(() => {
    reset(defaultValues)
  }, [defaultValues]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    void updateModelData(
      defaultValues ?? ({} as VentilationInputTabFormValues),
      0,
    )

    return () => {
      if (dbUpdateNeededRef.current && defaultValues) {
        void saveChanges()
        dbUpdateNeededRef.current = false
      }
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <form style={{ marginTop: theme.spacing(2) }}>
      {deviceDataRef.current.map((device, index) => (
        <Accordion
          key={`device-${index.toString()}`}
          elevation={0}
          classes={accordionClasses}
        >
          <AccordionSummary
            expandIcon={<ChevronDown id="collection-cooling-expand-button" />}
          >
            <Box display="flex" alignItems="center">
              <Typography>
                {t(
                  `${deviceDataRef.current[index].deviceName} - ${deviceDataRef.current[index].effectedArea}`,
                )}
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails style={{ flexDirection: 'column' }}>
            <Grid container spacing={2}>
              <Grid item xs>
                <ControlledTextField
                  key={`airFlow-${index.toString()}`}
                  control={control}
                  name={`values.parameters.ventilation.deviceData.${index}.airFlow`}
                  error={
                    errors.values?.parameters?.ventilation?.deviceData?.at(
                      index,
                    )?.airFlow
                  }
                  label={t('energySystem.parameters.ventilation.airFlow')}
                  rules={{
                    ...validators.required,
                    ...validators.positiveDecimalZero,
                  }}
                  InputProps={{
                    endAdornment: <Typography>{airFlowUnit}</Typography>,
                  }}
                  size="small"
                  onBlur={() => updateModelData(control.getValues(), index)}
                />
              </Grid>
              <Grid item xs>
                <ControlledTextField
                  key={`heatRecoveryEfficiency-${index.toString()}`}
                  control={control}
                  name={`values.parameters.ventilation.deviceData.${index}.heatRecoveryEfficiency`}
                  error={
                    errors.values?.parameters?.ventilation?.deviceData?.at(
                      index,
                    )?.heatRecoveryEfficiency
                  }
                  label={t(
                    'energySystem.parameters.ventilation.heatRecoveryEfficiency',
                  )}
                  rules={{
                    ...validators.required,
                    ...validators.efficiencyZero,
                  }}
                  size="small"
                  onBlur={() => updateModelData(control.getValues(), index)}
                />
              </Grid>
              <Grid item xs>
                <ControlledTextField
                  key={`SFP-${index.toString()}`}
                  control={control}
                  name={`values.parameters.ventilation.deviceData.${index}.SFP`}
                  error={
                    errors.values?.parameters?.ventilation?.deviceData?.at(
                      index,
                    )?.SFP
                  }
                  label={t('energySystem.parameters.ventilation.SFP')}
                  rules={{
                    ...validators.required,
                    ...validators.positiveDecimalZero,
                  }}
                  size="small"
                  onBlur={() => updateModelData(control.getValues(), index)}
                />
              </Grid>
              <Grid item xs>
                <ControlledTextField
                  key={`systemType-${index.toString()}`}
                  control={control}
                  name={`values.parameters.ventilation.deviceData.${index}.systemType`}
                  error={
                    errors.values?.parameters?.ventilation?.deviceData?.at(
                      index,
                    )?.systemType
                  }
                  label={t('energySystem.parameters.ventilation.systemType')}
                  rules={validators.required}
                  size="small"
                  select
                  onChange={async () => {
                    formState.isDirty = true
                    await updateModelData(control.getValues(), index)
                  }}
                >
                  {Object.values(VentilationDeviceType).map((vdType) => (
                    <MenuItem key={vdType} value={vdType}>
                      {t(
                        `energySystem.parameters.ventilation.systemTypes.${vdType}`,
                      )}
                    </MenuItem>
                  ))}
                </ControlledTextField>
              </Grid>
              <Grid item xs>
                <ControlledTextField
                  key={`deviceScheduleId-${index.toString()}`}
                  control={control}
                  name={`values.parameters.ventilation.deviceData.${index}.deviceScheduleId`}
                  error={
                    errors.values?.parameters?.ventilation?.deviceData?.at(
                      index,
                    )?.deviceScheduleId
                  }
                  label={t('energySystem.parameters.ventilation.usageProfile')}
                  rules={validators.required}
                  size="small"
                  disabled
                  select
                >
                  {Object.values(schedules).map((schedule) => (
                    <MenuItem key={schedule.id} value={schedule.id}>
                      {schedule.name}
                    </MenuItem>
                  ))}
                </ControlledTextField>
              </Grid>
            </Grid>

            <ScheduleHoursRow
              control={control}
              errors={errors}
              onUpdateModel={updateModelData}
              scheduleName="deviceScheduleValues"
              scheduleIndex={index}
            />

            <ScheduleMonthsRow
              control={control}
              errors={errors}
              onUpdateModel={updateModelData}
              scheduleName="deviceScheduleValues"
              scheduleIndex={index}
            />

            <Divider
              style={{
                marginTop: theme.spacing(2),
                marginBottom: theme.spacing(1),
              }}
            />
            <Grid container spacing={2} style={{ marginTop: theme.spacing(1) }}>
              <Grid item xs>
                <ControlledTextField
                  key={`demandBasedUsageCoefficient-${index.toString()}`}
                  control={control}
                  name={`values.parameters.ventilation.deviceData.${index}.demandBasedUsageCoefficient`}
                  error={
                    errors.values?.parameters?.ventilation?.deviceData?.at(
                      index,
                    )?.demandBasedUsageCoefficient
                  }
                  label={t(
                    'energySystem.parameters.ventilation.demandBasedUsageCoefficient',
                  )}
                  rules={{
                    ...validators.required,
                    ...validators.efficiency,
                  }}
                  size="small"
                  onBlur={() => updateModelData(control.getValues(), index)}
                />
              </Grid>
              <Grid item xs>
                <ControlledTextField
                  key={`exhaustAirMinimumTemp-${index.toString()}`}
                  control={control}
                  name={`values.parameters.ventilation.deviceData.${index}.exhaustAirMinimumTemp`}
                  error={
                    errors.values?.parameters?.ventilation?.deviceData?.at(
                      index,
                    )?.exhaustAirMinimumTemp
                  }
                  label={t(
                    'energySystem.parameters.ventilation.exhaustAirMinimumTemp',
                  )}
                  rules={{
                    ...validators.required,
                    ...validators.decimal,
                  }}
                  InputProps={{
                    endAdornment: <Typography>{temperatureUnit}</Typography>,
                  }}
                  size="small"
                  onBlur={() => updateModelData(control.getValues(), index)}
                />
              </Grid>
              <Grid item xs>
                <ControlledTextField
                  key={`coolingCoil-${index.toString()}`}
                  control={control}
                  name={`values.parameters.ventilation.deviceData.${index}.coolingCoil`}
                  error={
                    errors.values?.parameters?.ventilation?.deviceData?.at(
                      index,
                    )?.coolingCoil
                  }
                  label={t('energySystem.parameters.ventilation.coolingCoil')}
                  rules={validators.required}
                  size="small"
                  select
                  onChange={async () => {
                    formState.isDirty = true
                    await updateModelData(control.getValues(), index)
                  }}
                >
                  <MenuItem key="v-type-0" value={0}>
                    {t(
                      `energySystem.parameters.ventilation.dialog.existsSelection.${0}`,
                    )}
                  </MenuItem>
                  <MenuItem key="v-type-1" value={1}>
                    {t(
                      `energySystem.parameters.ventilation.dialog.existsSelection.${1}`,
                    )}
                  </MenuItem>
                </ControlledTextField>
              </Grid>
              <Grid item xs>
                <ControlledTextField
                  key={`supplyAirTemp-${index.toString()}`}
                  control={control}
                  name={`values.parameters.ventilation.deviceData.${index}.supplyAirTemp`}
                  error={
                    errors.values?.parameters?.ventilation?.deviceData?.at(
                      index,
                    )?.supplyAirTemp
                  }
                  label={t('energySystem.parameters.ventilation.supplyAirTemp')}
                  rules={{
                    ...validators.required,
                    ...validators.decimal,
                  }}
                  InputProps={{
                    endAdornment: <Typography>{temperatureUnit}</Typography>,
                  }}
                  size="small"
                  onBlur={() => updateModelData(control.getValues(), index)}
                />
              </Grid>
              <Grid item xs>
                <ControlledTextField
                  key={`indoorAirTemp-${index.toString()}`}
                  control={control}
                  name={`values.parameters.ventilation.deviceData.${index}.indoorAirTemp`}
                  error={
                    errors.values?.parameters?.ventilation?.deviceData?.at(
                      index,
                    )?.indoorAirTemp
                  }
                  label={t('energySystem.parameters.ventilation.indoorAirTemp')}
                  rules={{
                    ...validators.required,
                    ...validators.decimal,
                  }}
                  InputProps={{
                    endAdornment: <Typography>{temperatureUnit}</Typography>,
                  }}
                  size="small"
                  onBlur={() => updateModelData(control.getValues(), index)}
                />
              </Grid>
            </Grid>
            <Grid container spacing={2} style={{ marginTop: theme.spacing(1) }}>
              <Grid item xs>
                <ControlledTextField
                  key={`returnAirTemp-${index.toString()}`}
                  control={control}
                  name={`values.parameters.ventilation.deviceData.${index}.returnAirTemp`}
                  error={
                    errors.values?.parameters?.ventilation?.deviceData?.at(
                      index,
                    )?.returnAirTemp
                  }
                  label={t('energySystem.parameters.ventilation.returnAirTemp')}
                  rules={{
                    ...validators.required,
                    ...validators.decimal,
                  }}
                  InputProps={{
                    endAdornment: <Typography>{temperatureUnit}</Typography>,
                  }}
                  size="small"
                  onBlur={() => updateModelData(control.getValues(), index)}
                />
              </Grid>
              <Grid item xs>
                <ControlledTextField
                  key={`chilldrop-${index.toString()}`}
                  control={control}
                  name={`values.parameters.ventilation.deviceData.${index}.chilldrop`}
                  error={
                    errors.values?.parameters?.ventilation?.deviceData?.at(
                      index,
                    )?.chilldrop
                  }
                  label={t('energySystem.parameters.ventilation.chilldrop')}
                  rules={{
                    ...validators.required,
                    ...validators.decimal,
                  }}
                  InputProps={{
                    endAdornment: <Typography>%</Typography>,
                  }}
                  size="small"
                  onBlur={() => updateModelData(control.getValues(), index)}
                />
              </Grid>
              <Grid item xs>
                <ControlledTextField
                  key={`chilldropTempLimit-${index.toString()}`}
                  control={control}
                  name={`values.parameters.ventilation.deviceData.${index}.chilldropTempLimit`}
                  error={
                    errors.values?.parameters?.ventilation?.deviceData?.at(
                      index,
                    )?.chilldropTempLimit
                  }
                  label={t(
                    'energySystem.parameters.ventilation.chilldropTempLimit',
                  )}
                  rules={{
                    ...validators.required,
                    ...validators.decimal,
                  }}
                  InputProps={{
                    endAdornment: <Typography>{temperatureUnit}</Typography>,
                  }}
                  size="small"
                  onBlur={() => updateModelData(control.getValues(), index)}
                />
              </Grid>
              <Grid item xs>
                <ControlledTextField
                  key={`exhaustAirHeatPump-${index.toString()}`}
                  control={control}
                  name={`values.parameters.ventilation.deviceData.${index}.exhaustAirHeatPump`}
                  error={
                    errors.values?.parameters?.ventilation?.deviceData?.at(
                      index,
                    )?.exhaustAirHeatPump
                  }
                  label={t(
                    'energySystem.parameters.ventilation.exhaustAirHeatPump',
                  )}
                  rules={validators.required}
                  size="small"
                  select
                  onChange={() => updateModelData(control.getValues(), index)}
                >
                  <MenuItem key="v-type-0" value={0}>
                    {t(
                      `energySystem.parameters.ventilation.dialog.existsSelection.${0}`,
                    )}
                  </MenuItem>
                  <MenuItem key="v-type-1" value={1}>
                    {t(
                      `energySystem.parameters.ventilation.dialog.existsSelection.${1}`,
                    )}
                  </MenuItem>
                </ControlledTextField>
              </Grid>
              <Grid item xs />
            </Grid>
          </AccordionDetails>
        </Accordion>
      ))}
    </form>
  )
}
